import { supabase } from "@/lib/supabase";
import type { StorageUserFile, UploadFileOptions } from "@/types/storage";
export type { StorageUserFile, UploadFileOptions };

// ─── Public Types ────────────────────────────────────────────────────────────

export interface FileUpload {
  id: string;
  user_id: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  category: string;
  context_type: string | null;
  context_id: string | null;
  storage_path: string;
  share_scope: "private" | "school" | "district";
  district_id: string | null;
  school_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface FileValidationOptions {
  /** Allowed MIME types. Defaults to all supported types. */
  allowedTypes?: string[];
  /** Maximum file size in bytes. Defaults to 50 MB. */
  maxSizeBytes?: number;
}

export class StorageServiceError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = "StorageServiceError";
  }
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE_BUCKET = "user-files";
const DEFAULT_MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const DEFAULT_SIGNED_URL_EXPIRY_SECONDS = 3600; // 1 hour

export const ALL_SUPPORTED_MIME_TYPES: string[] = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "video/mp4",
  "video/webm",
  "audio/mpeg",
  "audio/wav",
  "audio/ogg",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

export const PDF_ONLY_MIME_TYPES: string[] = ["application/pdf"];

// ─── Internal Helpers ────────────────────────────────────────────────────────

const fileUploads = () => supabase.from("file_uploads");

/**
 * PostgREST returns code "PGRST205" when the table is missing from its
 * schema cache. That happens when a Supabase project hasn't run the
 * relevant migration (e.g. local-dev DBs that are behind the codebase),
 * or when the cache is briefly stale right after a schema change.
 *
 * For listing endpoints (getFilesByContext, getSnapshotsByContext,
 * getSchoolLibraryFiles, getUserFiles) we treat this as "feature not
 * provisioned on this DB" and return an empty result so the UI degrades
 * gracefully instead of showing a console error and breaking panels.
 */
const isMissingTableError = (
  err: { code?: string; message?: string } | null | undefined,
): boolean =>
  !!err &&
  (err.code === "PGRST205" ||
    /find the table|schema cache/i.test(err.message ?? ""));

const missingTableNoticesLogged = new Set<string>();
const noteMissingTableOnce = (table: string) => {
  if (missingTableNoticesLogged.has(table)) return;
  missingTableNoticesLogged.add(table);
  // Single, gentle warning per session per table — not an error spam.
  console.warn(
    `[storageService] '${table}' is not provisioned on this Supabase project. ` +
      `Returning empty results. If this is unexpected, run pending migrations or ` +
      `reload the PostgREST schema cache (Dashboard → Database → API → Reload schema).`,
  );
};

interface UserProfile {
  user_id: string;
  district_id: string | null;
  school_id: string | null;
}

// ─── Service ─────────────────────────────────────────────────────────────────

class StorageService {
  private async getUserProfile(): Promise<UserProfile | null> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const { data, error } = await supabase
      .from("profiles")
      .select("id, district_id, school_id")
      .eq("id", session.user.id)
      .maybeSingle();

    if (error || !data) return null;

    return {
      user_id: data.id,
      district_id: data.district_id,
      school_id: data.school_id,
    };
  }

  private buildStoragePath(
    profile: UserProfile,
    category: string,
    safeFileName: string,
  ): string {
    const district = profile.district_id ?? "no-district";
    const school = profile.school_id ?? (profile.district_id ? "district" : "no-school");
    return `${district}/${school}/${profile.user_id}/${category}/${safeFileName}`;
  }

  private validateFile(
    file: File,
    options: FileValidationOptions,
  ): StorageServiceError | null {
    const allowedTypes = options.allowedTypes ?? ALL_SUPPORTED_MIME_TYPES;
    const maxSize = options.maxSizeBytes ?? DEFAULT_MAX_SIZE_BYTES;

    if (!allowedTypes.includes(file.type)) {
      return new StorageServiceError(
        `File type "${file.type}" is not allowed. Allowed: ${allowedTypes.join(", ")}`,
        "INVALID_FILE_TYPE",
      );
    }

    if (file.size > maxSize) {
      return new StorageServiceError(
        `File size (${file.size} bytes) exceeds maximum allowed size of ${maxSize} bytes.`,
        "FILE_TOO_LARGE",
      );
    }

    return null;
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  /**
   * Upload a file to the user's private storage.
   * Validates the file client-side before uploading, then inserts a metadata
   * row into the file_uploads table.
   */
  uploadFile = async (
    file: File,
    category: string,
    contextType?: string,
    contextId?: string,
    validationOptions: FileValidationOptions = {},
    // Default share scope. Most callers leave files private; restoration
    // attachments override this to 'school' so colleagues at the same
    // school can see them in the Documents drawer.
    shareScope: "private" | "school" | "district" = "private",
  ): Promise<FileUpload> => {
    const validationError = this.validateFile(file, validationOptions);
    if (validationError) throw validationError;

    const profile = await this.getUserProfile();
    if (!profile) {
      throw new StorageServiceError("User is not authenticated.", "UNAUTHENTICATED");
    }

    const timestamp = Date.now();
    const safeFileName = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    const storagePath = this.buildStoragePath(profile, category, safeFileName);

    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(storagePath, file, { upsert: false });

    if (uploadError) {
      throw new StorageServiceError(
        `Blob upload failed: ${uploadError.message}`,
        "UPLOAD_FAILED",
      );
    }

    const { data, error: insertError } = await fileUploads()
      .insert({
        user_id: profile.user_id,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        category,
        context_type: contextType ?? null,
        context_id: contextId ?? null,
        storage_path: storagePath,
        share_scope: shareScope,
        district_id: profile.district_id,
        school_id: profile.school_id,
      })
      .select()
      .single();

    if (insertError) {
      // Roll back the blob so storage stays consistent with the metadata table.
      await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
      throw new StorageServiceError(
        `Metadata insert failed: ${insertError.message}`,
        "METADATA_INSERT_FAILED",
      );
    }

    return data as FileUpload;
  };

  /**
   * List the current user's own uploaded files, optionally filtered by category.
   */
  getUserFiles = async (category?: string): Promise<FileUpload[]> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user) return [];

    let query = fileUploads()
      .select("*")
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: false });

    if (category) {
      query = query.eq("category", category);
    }

    const { data, error } = await query;

    if (error) {
      if (isMissingTableError(error)) {
        noteMissingTableOnce("file_uploads");
        return [];
      }
      console.error("storageService.getUserFiles error:", error);
      return [];
    }

    return (data ?? []) as FileUpload[];
  };

  /**
   * List files shared at school or district scope that are visible to the
   * current user.
   */
  getSharedFiles = async (scope: "school" | "district"): Promise<FileUpload[]> => {
    const profile = await this.getUserProfile();
    if (!profile) return [];

    const scopeId = scope === "school" ? profile.school_id : profile.district_id;
    const scopeColumn = scope === "school" ? "school_id" : "district_id";

    if (!scopeId) return [];

    const { data, error } = await fileUploads()
      .select("*")
      .eq("share_scope", scope)
      .eq(scopeColumn, scopeId)
      .order("created_at", { ascending: false });

    if (error) {
      if (isMissingTableError(error)) {
        noteMissingTableOnce("file_uploads");
        return [];
      }
      console.error(`storageService.getSharedFiles(${scope}) error:`, error);
      return [];
    }

    return (data ?? []) as FileUpload[];
  };

  /**
   * Delete a file — removes both the blob from storage and the metadata row.
   */
  deleteFile = async (fileId: string): Promise<void> => {
    const { data: record, error: fetchError } = await fileUploads()
      .select("storage_path")
      .eq("id", fileId)
      .maybeSingle();

    if (fetchError) {
      throw new StorageServiceError(
        `Failed to fetch file record: ${fetchError.message}`,
        "FETCH_FAILED",
      );
    }
    if (!record) {
      throw new StorageServiceError(`File "${fileId}" not found.`, "NOT_FOUND");
    }

    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([record.storage_path]);

    if (storageError) {
      throw new StorageServiceError(
        `Blob deletion failed: ${storageError.message}`,
        "BLOB_DELETE_FAILED",
      );
    }

    const { error: deleteError } = await fileUploads().delete().eq("id", fileId);

    if (deleteError) {
      throw new StorageServiceError(
        `Metadata deletion failed: ${deleteError.message}`,
        "METADATA_DELETE_FAILED",
      );
    }
  };

  /**
   * Generate a time-limited signed URL for downloading or viewing a file.
   * Defaults to 1 hour expiry.
   */
  getSignedUrl = async (
    fileId: string,
    expiresIn: number = DEFAULT_SIGNED_URL_EXPIRY_SECONDS,
  ): Promise<string> => {
    const { data: record, error: fetchError } = await fileUploads()
      .select("storage_path")
      .eq("id", fileId)
      .maybeSingle();

    if (fetchError) {
      throw new StorageServiceError(
        `Failed to fetch file record: ${fetchError.message}`,
        "FETCH_FAILED",
      );
    }
    if (!record) {
      throw new StorageServiceError(`File "${fileId}" not found.`, "NOT_FOUND");
    }

    const { data, error: signError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(record.storage_path, expiresIn);

    if (signError || !data?.signedUrl) {
      throw new StorageServiceError(
        signError?.message ?? "Failed to generate signed URL.",
        "SIGNED_URL_FAILED",
      );
    }

    return data.signedUrl;
  };

  /**
   * Change the sharing scope of a file (private → school → district).
   */
  shareFile = async (
    fileId: string,
    scope: "private" | "school" | "district",
  ): Promise<void> => {
    const { error } = await fileUploads()
      .update({ share_scope: scope, updated_at: new Date().toISOString() })
      .eq("id", fileId);

    if (error) {
      throw new StorageServiceError(
        `Failed to update sharing scope: ${error.message}`,
        "SHARE_UPDATE_FAILED",
      );
    }
  };

  // ─── Snapshots / shared documents API ──────────────────────────────────────
  // Originally backed by `storage_user_files` (AE-50). The dev DB consolidated
  // this into `file_uploads` (AE-115 / migration 20260503185246), so all
  // snapshot calls now route through `file_uploads`. We keep the public
  // `StorageUserFile` shape the UI already consumes by mapping the rows below.

  /**
   * Map a `file_uploads` row to the legacy `StorageUserFile` shape so the
   * UI doesn't have to know which underlying table won. Renames the
   * `storage_path` column to `file_path` and derives `is_shared` from
   * `share_scope` (private = false; school/district = true).
   */
  private mapFileUploadToStorageUserFile = (
    row: Record<string, unknown>,
  ): StorageUserFile => {
    const scopeRaw = (row.share_scope as string | null) ?? "private";
    // `StorageUserFile.share_scope` only models 'private' | 'school' today.
    // Any 'district' rows surface as 'school' for sharing-toggle purposes —
    // they're still rendered; deeper district UX would need a type widening.
    const scope: "private" | "school" =
      scopeRaw === "private" ? "private" : "school";
    return {
      id: row.id as string,
      user_id: row.user_id as string,
      school_id: (row.school_id as string | null) ?? null,
      district_id: (row.district_id as string | null) ?? null,
      file_name: row.file_name as string,
      file_path: row.storage_path as string,
      category: (row.category as string) ?? "general",
      share_scope: scope,
      is_shared: scopeRaw !== "private",
      mime_type: (row.mime_type as string | null) ?? null,
      file_size: (row.file_size as number) ?? 0,
      context_type: (row.context_type as string | null) ?? null,
      context_id: (row.context_id as string | null) ?? null,
      created_at: row.created_at as string,
    };
  };

  /**
   * Upload a file and insert a `file_uploads` row.
   *
   * shareScope 'school'  → {districtId}/{schoolId}/shared/restoration/{ts}_{filename}
   *                        (matched by AE-50 storage policies)
   * shareScope 'private' → {userId}/private/{ts}_{filename}
   *
   * Filename sanitisation: non-[a-zA-Z0-9._-] chars replaced with _.
   */
  uploadFileToLibrary = async (
    file: File,
    options: UploadFileOptions = {},
  ): Promise<StorageUserFile> => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new StorageServiceError("User is not authenticated.", "UNAUTHENTICATED");
    }

    const {
      shareScope = "private",
      category = "general",
      schoolId,
      districtId,
      contextType,
      contextId,
    } = options;

    const timestamp = Date.now();
    const safeFileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;

    let filePath: string;
    if (shareScope === "school" && districtId && schoolId) {
      filePath = `${districtId}/${schoolId}/shared/restoration/${safeFileName}`;
    } else {
      filePath = `${user.id}/private/${safeFileName}`;
    }

    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(filePath, file, { contentType: file.type, upsert: false });

    if (uploadError) {
      throw new StorageServiceError(
        `Blob upload failed: ${uploadError.message}`,
        "UPLOAD_FAILED",
      );
    }

    const { data, error: insertError } = await fileUploads()
      .insert({
        user_id: user.id,
        school_id: schoolId ?? null,
        district_id: districtId ?? null,
        file_name: file.name,
        storage_path: filePath,
        category,
        share_scope: shareScope,
        mime_type: file.type || "application/octet-stream",
        file_size: file.size,
        context_type: contextType ?? null,
        context_id: contextId ?? null,
      })
      .select()
      .single();

    if (insertError) {
      // Roll back blob so storage stays consistent with metadata table.
      await supabase.storage.from(STORAGE_BUCKET).remove([filePath]);
      throw new StorageServiceError(
        `Metadata insert failed: ${insertError.message}`,
        "METADATA_INSERT_FAILED",
      );
    }

    return this.mapFileUploadToStorageUserFile(data as Record<string, unknown>);
  };

  /**
   * List files shared at school scope from `file_uploads`, optionally
   * filtered by category. Mapped to the `StorageUserFile` shape so callers
   * that expect the legacy snapshot type keep working.
   */
  getSchoolLibraryFiles = async (schoolId: string, category?: string): Promise<StorageUserFile[]> => {
    let query = fileUploads()
      .select("*")
      .eq("share_scope", "school")
      .eq("school_id", schoolId)
      .order("created_at", { ascending: false });

    if (category) {
      query = query.eq("category", category);
    }

    const { data, error } = await query;

    if (error) {
      if (isMissingTableError(error)) {
        noteMissingTableOnce("file_uploads");
        return [];
      }
      console.error("storageService.getSchoolLibraryFiles error:", error);
      return [];
    }

    return (data ?? []).map((row: Record<string, unknown>) =>
      this.mapFileUploadToStorageUserFile(row),
    ) as StorageUserFile[];
  };

  /**
   * List all file_uploads tied to a specific incident (or other
   * context). Relies on RLS to filter to rows the caller is allowed
   * to see (own + school-shared).
   */
  getFilesByContext = async (
    contextType: string,
    contextId: string,
  ): Promise<FileUpload[]> => {
    const { data, error } = await fileUploads()
      .select("*")
      .eq("context_type", contextType)
      .eq("context_id", contextId)
      // Chronological so the drawer reads as a timeline of the
      // incident — earliest evidence at the top, most recent at the
      // bottom (matching how the work was actually gathered).
      .order("created_at", { ascending: true });

    if (error) {
      if (isMissingTableError(error)) {
        noteMissingTableOnce("file_uploads");
        return [];
      }
      console.error("storageService.getFilesByContext error:", error);
      return [];
    }

    return (data ?? []) as FileUpload[];
  };

  /**
   * List all `file_uploads` rows tied to a specific incident, projected
   * back to the `StorageUserFile` shape callers expect. RLS filters to
   * rows the caller is allowed to see (own + school/district-shared).
   */
  getSnapshotsByContext = async (
    contextType: string,
    contextId: string,
  ): Promise<StorageUserFile[]> => {
    const { data, error } = await fileUploads()
      .select("*")
      .eq("context_type", contextType)
      .eq("context_id", contextId)
      // Chronological order — earliest snapshot first so revisions
      // read as a timeline.
      .order("created_at", { ascending: true });

    if (error) {
      if (isMissingTableError(error)) {
        noteMissingTableOnce("file_uploads");
        return [];
      }
      console.error("storageService.getSnapshotsByContext error:", error);
      return [];
    }

    return (data ?? []).map((row: Record<string, unknown>) =>
      this.mapFileUploadToStorageUserFile(row),
    ) as StorageUserFile[];
  };

  /**
   * Toggle a `file_uploads` row between private and school visibility.
   * The blob path doesn't move — only the metadata flag changes — so
   * other school members gain/lose access via the metadata-driven
   * storage RLS policy.
   */
  setSnapshotShareScope = async (
    fileId: string,
    scope: "private" | "school",
  ): Promise<void> => {
    const { error } = await fileUploads()
      .update({ share_scope: scope })
      .eq("id", fileId);

    if (error) {
      throw new StorageServiceError(
        `Failed to update snapshot scope: ${error.message}`,
        "SHARE_UPDATE_FAILED",
      );
    }
  };

  /**
   * Delete a `file_uploads` row + the underlying blob. Only the owner
   * can delete — RLS will reject the metadata delete otherwise.
   */
  deleteSnapshot = async (fileId: string): Promise<void> => {
    const { data: record, error: fetchError } = await fileUploads()
      .select("storage_path")
      .eq("id", fileId)
      .maybeSingle();

    if (fetchError) {
      throw new StorageServiceError(
        `Failed to fetch snapshot record: ${fetchError.message}`,
        "FETCH_FAILED",
      );
    }
    if (!record) {
      throw new StorageServiceError(`Snapshot "${fileId}" not found.`, "NOT_FOUND");
    }

    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([record.storage_path]);

    if (storageError) {
      throw new StorageServiceError(
        `Blob deletion failed: ${storageError.message}`,
        "BLOB_DELETE_FAILED",
      );
    }

    const { error: deleteError } = await fileUploads()
      .delete()
      .eq("id", fileId);

    if (deleteError) {
      throw new StorageServiceError(
        `Metadata deletion failed: ${deleteError.message}`,
        "METADATA_DELETE_FAILED",
      );
    }
  };

  /**
   * Generate a signed URL directly from a storage file path.
   */
  getSignedUrlFromPath = async (
    filePath: string,
    expiresIn: number = DEFAULT_SIGNED_URL_EXPIRY_SECONDS,
  ): Promise<string> => {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(filePath, expiresIn);

    if (error || !data?.signedUrl) {
      throw new StorageServiceError(
        error?.message ?? "Failed to generate signed URL.",
        "SIGNED_URL_FAILED",
      );
    }

    return data.signedUrl;
  };
}

export const storageService = new StorageService();
